import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Keyboard } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';

interface Shortcut {
  keys: string[];
  description: string;
}

const SHORTCUTS: { group: string; items: Shortcut[] }[] = [
  {
    group: 'General',
    items: [
      { keys: ['?'], description: 'Show keyboard shortcuts' },
      { keys: ['Esc'], description: 'Close menus, modals, and panels' },
      { keys: ['Tab'], description: 'Reveal "Skip to main content" link' },
    ],
  },
  {
    group: 'Navigation',
    items: [
      { keys: ['Ctrl', 'B'], description: 'Toggle sidebar (instructor / admin)' },
    ],
  },
  {
    group: 'Editing',
    items: [
      { keys: ['Click', 'mic'], description: 'Start / stop voice dictation in any long-form field' },
      { keys: ['Click', 'speaker'], description: 'Read AI replies aloud' },
    ],
  },
];

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

export function KeyboardShortcutsOverlay() {
  const [open, setOpen] = useState(false);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Esc always closes the overlay if open
      if (e.key === 'Escape' && open) {
        close();
        return;
      }
      // Ctrl/Cmd+B toggles sidebar (even from inputs)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        toggleSidebar();
        return;
      }
      // Ignore other shortcuts while typing
      if (isTypingTarget(e.target)) return;
      // Shift+/ produces "?" — open the overlay
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onShow = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('shopcommand:show-shortcuts', onShow);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('shopcommand:show-shortcuts', onShow);
    };
  }, [open, close, toggleSidebar]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="kbd-shortcuts-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={close}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Keyboard className="w-4 h-4 text-[var(--color-accent-primary)]" />
            <h2 id="kbd-shortcuts-title" className="text-sm font-semibold text-[var(--color-text-primary)]">
              Keyboard shortcuts
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="p-1 rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {SHORTCUTS.map((g) => (
            <section key={g.group}>
              <h3 className="text-[10px] uppercase tracking-wide font-semibold text-[var(--color-text-secondary)] mb-2">
                {g.group}
              </h3>
              <ul className="space-y-1.5">
                {g.items.map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-[var(--color-text-primary)]">{s.description}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {s.keys.map((k, j) => (
                        <span key={j} className="inline-flex items-center">
                          {j > 0 && <span className="mx-1 text-[var(--color-text-secondary)] text-xs">+</span>}
                          <kbd className="px-1.5 py-0.5 text-[11px] font-mono font-medium rounded border border-[var(--color-border)] bg-[var(--color-bg-tertiary)] text-[var(--color-text-primary)] shadow-sm">
                            {k}
                          </kbd>
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <p className="text-xs text-[var(--color-text-secondary)] pt-2 border-t border-[var(--color-border)]">
            Press <kbd className="px-1 py-0.5 text-[10px] font-mono rounded border border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">?</kbd> anywhere to reopen this panel.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
